import React from 'react';
import { Seo } from '../components/Seo';
import { useSiteContacts } from '@/hooks/useSiteContacts';

import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, ChevronDown } from 'lucide-react';
import { useT } from '@/context/ContentContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

export const Contact: React.FC = () => {
  const contacts = useSiteContacts();
  const title = useT('contact.title', 'קביעת תור וייעוץ');
  const formHeading = useT('contact.form.heading', 'השאירי פרטים');
  const addressLabel = useT('contact.address.label', 'כתובת הקליניקה');
  const phoneLabel = useT('contact.phone.label', 'טלפון');
  const hoursLabel = useT('contact.hours.label', 'שעות פעילות');
  return (
    <>
      <Seo
        title="קביעת תור וייעוץ | RIHAM קליניקה במושב פטיש"
        description="צרי קשר עם הקליניקה של רהאם מנסור במושב פטיש לקביעת תור, ייעוץ אישי ובחירת טיפול מתאים. כתובת, טלפון ושעות פעילות."
        path="/contact"
      />
    <div className="bg-brand-sand min-h-[100dvh] pt-32 pb-24 text-brand-bark">
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-brand-gold text-sm tracking-[0.3em] uppercase block mb-4 font-sans font-light">יצירת קשר</span>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif mb-8 text-brand-bark">{title}</h1>
          <p className="text-brand-bark/70 max-w-2xl mx-auto font-sans font-light text-lg">
            אנו מזמינים אותך ליצור קשר לקביעת פגישת ייעוץ והתאמה אישית של טיפול או מוצר.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card className="bg-white border-brand-bark/10 shadow-brand">
              <CardHeader>
                <CardTitle className="font-sans font-medium text-2xl text-brand-bark text-start">{formHeading}</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-6 text-start text-brand-bark" dir="rtl">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-sans font-light text-brand-bark">שם מלא</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="הכניסי את שמך"
                      className="border-brand-bark/20 focus-visible:ring-brand-gold bg-white text-start placeholder:text-brand-bark/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-sans font-light text-brand-bark">טלפון</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="מספר טלפון לחזרה"
                      className="border-brand-bark/20 focus-visible:ring-brand-gold bg-white text-start placeholder:text-brand-bark/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="font-sans font-light text-brand-bark">נושא הפנייה</Label>
                    <div className="relative">
                      <select
                        id="subject"
                        className="flex h-10 w-full rounded-md border border-brand-bark/20 bg-white px-3 py-2 text-base font-sans text-brand-bark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 appearance-none text-start"
                      >
                        <option value="">בחרי נושא...</option>
                        <option value="hair">טיפולי שיער</option>
                        <option value="face">טיפולי פנים וקוסמטיקה</option>
                        <option value="products">רכישת מוצרים</option>
                        <option value="other">אחר</option>
                      </select>
                      <ChevronDown className="absolute top-1/2 -translate-y-1/2 left-3 size-4 text-brand-bark/50 pointer-events-none" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-sans font-light text-brand-bark">הודעה (אופציונלי)</Label>
                    <Textarea
                      id="message"
                      rows={4}
                      placeholder="ספרי לנו קצת על מה שאת מחפשת..."
                      className="border-brand-bark/20 focus-visible:ring-brand-gold bg-white resize-none text-start placeholder:text-brand-bark/50"
                    />
                  </div>
                  <Button
                    type="button"
                    className="w-full bg-brand-gold text-brand-bark hover:bg-brand-gold/90 h-auto py-4 uppercase tracking-widest font-sans font-medium text-sm"
                  >
                    שליחה
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col justify-between"
          >
            <div className="space-y-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="bg-brand-gold/15 p-3 rounded-full text-brand-gold shrink-0">
                  <MapPin size={24} />
                </div>
                <div className="text-start">
                  <h3 className="text-xl font-sans font-medium mb-1 text-brand-bark">{addressLabel}</h3>
                  <p className="text-brand-bark font-sans font-light">{contacts.addressHe}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-brand-gold/15 p-3 rounded-full text-brand-gold shrink-0">
                  <Phone size={24} />
                </div>
                <div className="text-start">
                  <h3 className="text-xl font-sans font-medium mb-1 text-brand-bark">{phoneLabel}</h3>
                  <a href={`tel:${contacts.telephone}`} className="text-brand-bark font-sans font-light hover:underline">{contacts.telephoneDisplay}</a>
                  <div className="mt-1"><a href={`mailto:${contacts.email}`} className="text-brand-bark font-sans font-light hover:underline">{contacts.email}</a></div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-brand-gold/15 p-3 rounded-full text-brand-gold shrink-0">
                  <Clock size={24} />
                </div>
                <div className="text-start">
                  <h3 className="text-xl font-sans font-medium mb-1 text-brand-bark">{hoursLabel}</h3>
                  <p className="text-brand-bark font-sans font-light whitespace-pre-line">{contacts.openingHoursDisplay}</p>

                </div>
              </div>
            </div>

            {/* Map */}
            <Card className="overflow-hidden border-brand-bark/10 shadow-brand">
              <div className="w-full h-64 relative">
                <div className="absolute inset-0 flex items-center justify-center text-brand-bark/40 font-sans font-light text-xl z-10 pointer-events-none bg-brand-cream/50 group-hover:opacity-0 transition-opacity">
                  מפת הגעה
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3396.123456789!2d34.56789!3d31.23456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzHCsDE0JzA0LjQiTiAzNMKwMzQnMDQuNCJF!5e0!3m2!1sen!2sil!4v1612345678901!5m2!1sen!2sil"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  className="opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                ></iframe>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};
